/**
 * Where MSG-030's magic link lands after sign-in (PRA-024, AUT-020, PRA-031). Claiming the
 * clinic record sends the patient straight to checkout.
 */
import { useEffect, useState } from "react";
import { Button } from "@lattice/ui/Button";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Pending } from "@lattice/ui/Pending";
import { claimPatient, loadCheckout, signedIn, signedInEmail, signOut } from "./api.ts";
import { AuthLayout } from "./AuthLayout.tsx";
import { navigate } from "./router.ts";

const COPY = {
  missing: {
    heading: "This invitation is incomplete",
    body: "Open the button in the email from your clinic. It signs you in and brings you here.",
  },
  pending: {
    heading: "Opening your formula",
    body: "We are opening the formula your clinic made.",
  },
  signed_out: {
    heading: "Sign in to open your formula",
    body: "Use the button in your clinic's email, or sign in with the address they invited. Each link works once, for 24 hours. If yours ran out, ask your clinic for a new one.",
  },
  email_mismatch: {
    heading: "This invitation is for a different email",
    body: "Sign in with the address your clinic invited, then open the invitation email again.",
  },
  expired: {
    heading: "This invitation has expired",
    body: "Ask your clinic to send a new one. Invitations last 24 hours.",
  },
  already_claimed: {
    heading: "This invitation was already used",
    body: "If that's you, sign in from your email and go to checkout.",
  },
  verify_email_first: {
    heading: "Confirm your email first",
    body: "This invitation opens only for an account with a confirmed email. Sign in from the button in your clinic's email, which confirms it.",
  },
  unreachable: {
    heading: "This page didn't load",
    body: "Try again in a minute. If it keeps happening, write to support.",
  },
  failed: {
    heading: "We couldn't open this formula",
    body: "Try the button in your email again, or ask your clinic to resend the invitation.",
  },
} as const;

function copyFor(error: string | null): (typeof COPY)[keyof typeof COPY] {
  if (error === null) return COPY.pending;

  if (error === "missing") return COPY.missing;

  if (error === "signed_out") return COPY.signed_out;

  if (error === "email_mismatch") return COPY.email_mismatch;

  if (error === "expired") return COPY.expired;

  if (error === "already_claimed") return COPY.already_claimed;

  if (error === "verify_email_first") return COPY.verify_email_first;

  if (error === "unreachable") return COPY.unreachable;

  return COPY.failed;
}

function goToCheckout() {
  navigate("/checkout");
}

/** What a person can do next from each dead end, so no error leaves them with nothing to press. */
function Next({ error, token }: { readonly error: string | null; readonly token: string }) {
  if (error === null) return <Pending message="Opening your formula" bones={["control"]} />;

  if (error === "unreachable")
    return (
      <>
        <Button onClick={() => window.location.reload()}>Try again</Button>
        <LinkButton href="/support" tone="quiet">
          Contact support
        </LinkButton>
      </>
    );

  if (error === "email_mismatch") {
    const backHere = `/sign-in?next=${encodeURIComponent(`/claim?token=${token}`)}`;

    return (
      <Button
        onClick={() => {
          void signOut().then(
            () => navigate(backHere),
            () => navigate(backHere),
          );
        }}
      >
        Sign out and use the invited address
      </Button>
    );
  }

  if (error === "signed_out")
    return (
      <LinkButton href={`/sign-in?next=${encodeURIComponent(`/claim?token=${token}`)}`}>
        Sign in
      </LinkButton>
    );

  if (error === "verify_email_first" || error === "already_claimed")
    return <LinkButton href="/sign-in">Sign in</LinkButton>;

  return <LinkButton href="/support">Contact support</LinkButton>;
}

export function Claim() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [error, setError] = useState<string | null>(token === "" ? "missing" : null);
  const [address, setAddress] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Claim your formula · Lattice";
  }, []);

  useEffect(() => {
    if (token === "") return;
    let cancelled = false;

    const run = async () => {
      try {
        await attempt();
      } catch {
        if (!cancelled) setError("unreachable");
      }
    };

    const attempt = async () => {
      if (!(await signedIn())) {
        if (!cancelled) setError("signed_out");

        return;
      }

      const claimed = await claimPatient(token);

      if (cancelled) return;

      if (claimed.ok) {
        goToCheckout();

        return;
      }

      if (claimed.error === "not_found" || claimed.error === "already_claimed") {
        const checkout = await loadCheckout();

        if (checkout !== "signed_out" && checkout !== "missing" && checkout !== "failed") {
          goToCheckout();

          return;
        }
      }

      /* Only the address they are signed in with is named. The invited one belongs to someone
         else, and this page is what a stranger holding the link would see. */
      if (claimed.error === "email_mismatch") {
        const email = await signedInEmail();

        if (!cancelled) setAddress(email);
      }

      if (!cancelled) setError(claimed.error);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const copy = copyFor(error);

  const body =
    error === "email_mismatch" && address !== null
      ? `You're signed in as ${address}. ${copy.body}`
      : copy.body;

  return (
    <AuthLayout
      key={error ?? "pending"}
      title={copy.heading}
      lead={body}
      focusHeading={error !== null}
      alert={error !== null && error !== "signed_out" && error !== "missing"}
      testId="claim-page"
    >
      <Next error={error} token={token} />
    </AuthLayout>
  );
}
